import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Calculator, Upload, Image as ImageIcon } from 'lucide-react';
import { API_URL } from '../../services/api';
import PierresEditor from './PierresEditor';

const CreateDevis = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const modalBodyRef = useRef(null);

  // Remonter en haut du modal à chaque changement d'étape
  const goToStep = (n) => {
    setStep(n);
    setTimeout(() => {
      if (modalBodyRef.current) modalBodyRef.current.scrollTop = 0;
    }, 0);
  };
  const [formData, setFormData] = useState({
    type_creation: 'BIJOUTERIE',
    client_nom: '',
    client_prenom: '',
    reference_article: '',
    categorie_id: '',
    collection_nom: '',
    type_bijou: 'BAGUE',
    couleur_or: 'JAUNE',
    description: '',
    notes: '',
    image_url: '',
    poids_metal_estime: 0,
    coefficient_marge: 2.6,
    marge_globale_active: true,
    date_livraison_souhaitee: '',
    perte_or_pct: 0.10
  });

  const [bijouPhotos, setBijouPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);
  
  const [packagingSelectionne, setPackagingSelectionne] = useState([]);

  const [matieres, setMatieres] = useState([]);
  const [pierres, setPierres] = useState([{ reference: '', type: '', poids_carats: '', quantite: 1, marge_individuelle: 0 }]);
  const [ouvriers, setOuvriers] = useState([{ ouvrier_id: '', temps_minutes: 0, marge_individuelle: 0 }]);
  const [facon, setFacon] = useState({ active: false, coefficient: '', description: "Façon (main d'œuvre)" });
  const [coutsFabrication, setCoutsFabrication] = useState([]);
  const [composants, setComposants] = useState([]);
  const [margesLignes, setMargesLignes] = useState({ matieres: 2.5, pierres: 2.5, ouvriers: 2.5, composants: 2.5 });

  const [availableMatieres, setAvailableMatieres] = useState([]);
  const [availableOuvriers, setAvailableOuvriers] = useState([]);
  const [availableCouts, setAvailableCouts] = useState([]);
  const [availablePackaging, setAvailablePackaging] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [availableTypesBijoux, setAvailableTypesBijoux] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [prixActuels, setPrixActuels] = useState({ or: 0, chaine: 0, mousqueton: 0 });
  const [matieresSuggestions, setMatieresSuggestions] = useState([]);
  const [showMatieresSuggestions, setShowMatieresSuggestions] = useState(-1);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [availableBoutiques, setAvailableBoutiques] = useState([]);

  const [prixCalcule, setPrixCalcule] = useState({
    matieres: 0,
    pierres: 0,
    ouvriers: 0,
    facon: 0,
    fabrication: 0,
    composants: 0,
    perte: 0,
    total: 0,
    avec_marge: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    calculerPrix();
  }, [formData, matieres, pierres, ouvriers, facon, coutsFabrication, composants, prixActuels, margesLignes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calcul poids métal depuis les matières OR uniquement
  useEffect(() => {
    const totalPoids = matieres.reduce((sum, m) => {
      const t = (m.type || '').toUpperCase();
      const r = (m.reference || '').toUpperCase();
      const d = (m.designation || '').toUpperCase();
      if (t.includes('OR') || r.includes('OR') || d.includes('OR 18')) {
        return sum + parseFloat(m.quantite || 0);
      }
      return sum;
    }, 0);
    if (totalPoids > 0) {
      setFormData(f => ({ ...f, poids_metal_estime: totalPoids }));
    }
  }, [matieres]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      // Charger prix actuels
      const prixRes = await fetch(`${API_URL}/stock-matieres/prix/actuel`);
      const prixData = await prixRes.json();
      setPrixActuels(prixData);

      // Charger ouvriers
      const ouvriersRes = await fetch(`${API_URL}/ouvriers`);
      const ouvriersData = await ouvriersRes.json();
      setAvailableOuvriers(Array.isArray(ouvriersData) ? ouvriersData : []);

      // Charger coûts de fabrication
      const coutsRes = await fetch(`${API_URL}/fabrication/couts`);
      const coutsData = await coutsRes.json();
      setAvailableCouts(Array.isArray(coutsData) ? coutsData : []);

      // Charger types de packaging disponibles
      const packagingRes = await fetch(`${API_URL}/packaging/stock/disponible`);
      const packagingData = await packagingRes.json();
      setAvailablePackaging(Array.isArray(packagingData) ? packagingData : []);

      // Charger collections
      const collectionsRes = await fetch(`${API_URL}/collections`);
      const collectionsData = await collectionsRes.json();
      setAvailableCollections(Array.isArray(collectionsData) ? collectionsData : []);

      // Charger types de bijoux
      const typesRes = await fetch(`${API_URL}/collections/types-bijoux`);
      const typesData = await typesRes.json();
      setAvailableTypesBijoux(Array.isArray(typesData) ? typesData : []);

      // Charger catégories de bijoux
      const categoriesRes = await fetch(`${API_URL}/collections/categories-bijoux`);
      const categoriesData = await categoriesRes.json();
      setAvailableCategories(Array.isArray(categoriesData) ? categoriesData : []);

      // Charger boutiques
      try {
        const boutiquesRes = await fetch(`${API_URL}/boutiques`);
        const boutiquesData = await boutiquesRes.json();
        setAvailableBoutiques(Array.isArray(boutiquesData) ? boutiquesData : []);
      } catch {}
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const calculerPrix = () => {
    let totalMatieres = 0;
    let totalPierres = 0;
    let totalOuvriers = 0;
    let totalFabrication = 0;
    let totalComposants = 0;
    let poidsOrTotal = 0;

    // Calcul matières
    let prixOrDevis = 0; // prix unitaire de l'or tel que saisi dans le devis
    matieres.forEach(m => {
      const prixLigne = parseFloat(m.quantite || 0) * parseFloat(m.prix_unitaire || 0);
      totalMatieres += prixLigne;
      const mType = (m.type || '').toUpperCase();
      const mRef = (m.reference || '').toUpperCase();
      const mDesig = (m.designation || '').toUpperCase();
      if (mType.includes('OR') || mRef.includes('OR') || mDesig.includes('OR 18')) {
        poidsOrTotal += parseFloat(m.quantite || 0);
        // Prendre le prix unitaire de l'or du devis (pas le prix actuel)
        if (parseFloat(m.prix_unitaire || 0) > 0) {
          prixOrDevis = parseFloat(m.prix_unitaire);
        }
      }
    });

    // Calcul pierres
    pierres.forEach(p => {
      if (parseFloat(p.poids_carats || 0) > 0 && parseFloat(p.prix_achat_unitaire || 0) > 0) {
        totalPierres += parseFloat(p.poids_carats || 0) * parseFloat(p.prix_achat_unitaire || 0) * parseFloat(p.quantite || 1);
      }
    });

    // Calcul ouvriers
    ouvriers.forEach(o => {
      const ouvrier = availableOuvriers.find(av => av.id === parseInt(o.ouvrier_id));
      if (ouvrier && o.temps_minutes > 0) {
        totalOuvriers += parseFloat(o.temps_minutes || 0) * parseFloat(ouvrier.cout_minute || 0);
      }
    });

    // Calcul coûts fabrication (séparés interne / externe)
    // Les étapes POIDS sont des coûts de traitement au gramme (ex: Fonte 7 DH/g × poids or)
    // Elles ne COMPTENT PAS dans poidsOrTotal (c'est du traitement, pas du métal)
    let totalFabInterne = 0, totalFabExterne = 0;
    coutsFabrication.forEach(c => {
      if (c.type_ressource === 'interne') totalFabInterne += parseFloat(c.prix_total || 0);
      else totalFabExterne += parseFloat(c.prix_total || 0);
    });

    // Calcul composants (séparés interne / externe)
    // Les composants de type POIDS (chaines, fermoirs en or) comptent dans le poids or total
    let totalCompInterne = 0, totalCompExterne = 0;
    composants.forEach(c => {
      if (c.type_ressource === 'interne') totalCompInterne += parseFloat(c.prix_total || 0);
      else totalCompExterne += parseFloat(c.prix_total || 0);
      // Les composants POIDS (chaines, mousquetons) ne comptent PAS dans la perte or
      // Seules les matieres de type OR comptent pour la perte
    });

    const totalInterne = totalFabInterne + totalCompInterne;
    const totalExterne = totalFabExterne + totalCompExterne;
    totalFabrication = totalInterne + totalExterne; // backward compat

    // Façon (alternative aux ouvriers)
    let coutFacon = 0;
    if (facon.active && facon.coefficient && formData.poids_metal_estime) {
      coutFacon = parseFloat(facon.coefficient) * parseFloat(formData.poids_metal_estime || 0);
    }

    // Calcul de la perte or (taux configurable, sur le prix or du devis)
    const pertePct = parseFloat(formData.perte_or_pct || 0.10);
    const prixOrPourPerte = prixOrDevis > 0 ? prixOrDevis : parseFloat(prixActuels.or || 0);
    const pertePoids = poidsOrTotal * pertePct;
    const prixPerte = pertePoids * prixOrPourPerte;

    const total = totalMatieres + totalPierres + totalOuvriers + coutFacon + totalInterne + totalExterne + prixPerte;

    // Convertir le coefficient en nombre
    let coefficient = formData.coefficient_marge;
    if (typeof coefficient === 'string') {
      coefficient = parseFloat(coefficient.replace(',', '.'));
    } else {
      coefficient = parseFloat(coefficient);
    }
    if (isNaN(coefficient) || coefficient <= 0) coefficient = 2.6;

    // Calculer le prix avec marge
    let avecMarge;
    if (formData.marge_globale_active) {
      avecMarge = total * coefficient;
    } else {
      // Marge par ligne (coefficients séparés pour interne/externe)
      avecMarge =
        totalMatieres * (margesLignes.matieres || 1) +
        totalPierres  * (margesLignes.pierres   || 1) +
        (totalOuvriers + coutFacon + totalInterne) * (margesLignes.ouvriers  || 1) +
        totalExterne  * (margesLignes.composants || 1) +
        prixPerte;
    }

    setPrixCalcule({
      matieres: totalMatieres,
      pierres: totalPierres,
      ouvriers: totalOuvriers,
      facon: coutFacon,
      fabrication: totalFabrication,
      fabrication_interne: totalInterne,
      fabrication_externe: totalExterne,
      composants: totalComposants,
      perte: prixPerte,
      total: total,
      avec_marge: avecMarge,
      poids_or_total: poidsOrTotal,
      perte_poids: pertePoids,
      perte_pct: pertePct,
      prix_or_utilise: prixOrPourPerte
    });
  };

  const addMatiere = () => {
    setMatieres([...matieres, { reference: '', type: 'OR 18K', quantite: '', prix_unitaire: prixActuels.or, stock_id: null, marge_individuelle: 0 }]);
  };

  const searchMatieres = async (query, index) => {
    if (!query || query.length < 1) {
      setMatieresSuggestions([]);
      setShowMatieresSuggestions(-1);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/stock-matieres/search-matieres?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setMatieresSuggestions(data);
      setShowMatieresSuggestions(index);
    } catch (error) {
      console.error('Erreur recherche matières:', error);
      setMatieresSuggestions([]);
    }
  };

  const searchClients = async (query) => {
    if (!query || query.length < 2) { setClientSuggestions([]); setShowClientSuggestions(false); return; }
    try {
      const res = await fetch(`${API_URL}/clients?search=${encodeURIComponent(query)}`);
      const data = await res.json();
      setClientSuggestions(data);
      setShowClientSuggestions(data.length > 0);
    } catch { setClientSuggestions([]); }
  };

  const selectClient = (client) => {
    setFormData(f => ({ ...f, client_nom: client.nom || '', client_prenom: '' }));
    setClientSuggestions([]);
    setShowClientSuggestions(false);
  };

  const selectMatiere = (matiere, index) => {
    const newMatieres = [...matieres];
    newMatieres[index] = {
      reference: matiere.reference,
      type: matiere.type_matiere,
      quantite: '', // L'utilisateur saisira le poids dont il a besoin
      prix_unitaire: matiere.prix_achat_unitaire,
      stock_id: matiere.id,
      designation: matiere.designation,
      stock_disponible: parseFloat(matiere.quantite_disponible || 0) // Pour afficher le stock disponible
    };
    setMatieres(newMatieres);
    setMatieresSuggestions([]);
    setShowMatieresSuggestions(-1);
  };


  const handlePhotosUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setBijouPhotos([...bijouPhotos, ...files]);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setPhotosPreviews([...photosPreviews, ...newPreviews]);
    }
  };

  const removePhoto = (index) => {
    const newPhotos = bijouPhotos.filter((_, i) => i !== index);
    const newPreviews = photosPreviews.filter((_, i) => i !== index);
    setBijouPhotos(newPhotos);
    setPhotosPreviews(newPreviews);
  };

  const addOuvrier = () => {
    setOuvriers([...ouvriers, { ouvrier_id: '', temps_minutes: 0, marge_individuelle: 0 }]);
  };

  const addCoutFabrication = (cout) => {
    const existing = coutsFabrication.find(c => c.cout_id === cout.id);
    if (!existing) {
      let prixUnit = parseFloat(cout.cout) || 0;
      let qteDefaut = 1;
      // Si type POIDS → quantité = poids or total, prix = tarif/g de l'étape (pas le prix or)
      // Ex: Fonte 7 DH/g × 14g = 98 DH
      if (cout.type_cout === 'POIDS') {
        qteDefaut = parseFloat(formData.poids_metal_estime) || 0;
        // On garde cout.cout comme prix_unitaire (c'est le tarif au gramme du procédé)
      }
      setCoutsFabrication([...coutsFabrication, {
        cout_id: cout.id,
        nom: cout.nom,
        type_cout: cout.type_cout,
        type_ressource: cout.type_ressource || 'externe',
        quantite: qteDefaut,
        prix_unitaire: prixUnit,
        prix_total: qteDefaut * prixUnit
      }]);
    }
  };

  const addComposant = (cout) => {
    const existing = composants.find(c => c.cout_id === cout.id);
    if (!existing) {
      // Déterminer la quantité par défaut selon le type
      let quantiteDefaut = 1;
      let prixUnit = parseFloat(cout.cout) || 0;

      if (cout.type_cout === 'POIDS') {
        // Pré-remplir avec le poids estimé du bijou
        quantiteDefaut = parseFloat(formData.poids_metal_estime) || 0;
        // Prix au gramme = prix d'achat du composant (chaque item a son propre prix fournisseur)
        // Le coef de marge s'applique ensuite globalement
      } else if (cout.type_cout === 'MINUTE') {
        quantiteDefaut = 0; // L'utilisateur saisira le temps
      } else {
        quantiteDefaut = 1; // Prix fixe
      }

      setComposants([...composants, {
        cout_id: cout.id,
        nom: cout.nom,
        type_cout: cout.type_cout,
        type_ressource: cout.type_ressource || 'externe',
        unite: cout.type_cout === 'POIDS' ? 'g' : cout.type_cout === 'MINUTE' ? 'min' : 'unité',
        quantite: quantiteDefaut,
        prix_unitaire: prixUnit,
        prix_total: quantiteDefaut * prixUnit,
        marge_individuelle: 0
      }]);
    }
  };

  const updateComposantQuantite = (index, quantite) => {
    const newComposants = [...composants];
    newComposants[index].quantite = quantite;
    newComposants[index].prix_total = quantite * newComposants[index].prix_unitaire;
    setComposants(newComposants);
  };

  const updateComposantPrix = (index, prix) => {
    const newComposants = [...composants];
    newComposants[index].prix_unitaire = prix;
    newComposants[index].prix_total = newComposants[index].quantite * prix;
    setComposants(newComposants);
  };

  const removeComposant = (index) => {
    setComposants(composants.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.type_bijou) {
      alert('⚠️ Le type de bijou est obligatoire.');
      goToStep(1);
      return;
    }
    try {
      // Uploader l'image si presente
      let imageUrl = formData.image_url || '';
      if (bijouPhotos.length > 0 && !imageUrl) {
        const fd = new FormData();
        fd.append('image', bijouPhotos[0]);
        fd.append('type_creation', formData.type_creation || 'CLIENT');
        fd.append('type_bijou', formData.type_bijou || 'bague');
        fd.append('collection_nom', formData.collection_nom || '');
        const uploadRes = await fetch(`${API_URL}/bijoux-images/upload`, { method: 'POST', body: fd });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          imageUrl = `/uploads/bijoux/${uploadData.path}`;
        }
      }

      const devisData = {
        ...formData,
        image_url: imageUrl,
        matieres,
        pierres,
        ouvriers,
        facon: facon.active ? { coefficient: parseFloat(facon.coefficient || 0), actif: true } : null,
        couts_fabrication: [
          ...coutsFabrication,
          ...composants.map(c => ({ id: c.cout_id, quantite: c.quantite, cout: c.prix_unitaire, prix_total: c.prix_total }))
        ],
        packaging: packagingSelectionne,
        prix_total_theorique: prixCalcule.total,
        prix_vente_final: prixCalcule.avec_marge,
        marges_lignes: margesLignes
      };

      console.log('📦 Données du devis:', devisData);

      const response = await fetch(`${API_URL}/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(devisData)
      });

      console.log('📡 Réponse serveur:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Devis cree:', result);
        if (result.warnings && result.warnings.length > 0) {
          alert('Devis cree avec avertissements :\n\n' + result.warnings.join('\n'));
        } else {
          alert('Devis cree avec succes !');
        }
        onSuccess();
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur serveur:', errorData);
        alert(`Erreur lors de la création du devis: ${errorData.error || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      alert(`Erreur lors de la création du devis: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
      <div ref={modalBodyRef} className="bg-white rounded-t-2xl sm:rounded-lg p-6 max-w-7xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto sm:m-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Créer un Nouveau Devis</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 self-end sm:self-auto">
            <X size={24} />
          </button>
        </div>

        {/* Étapes */}
        <div className="flex mb-6 space-x-2 overflow-x-auto">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={`flex-1 h-2 rounded ${step >= s ? 'bg-blue-600' : 'bg-gray-200'}`}
            />
          ))}
        </div>

        {/* Étape 1: Informations générales */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Informations Générales</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de Création *</label>
                <select
                  value={formData.type_creation}
                  onChange={(e) => setFormData({...formData, type_creation: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="BIJOUTERIE">Pour la Bijouterie</option>
                  <option value="CLIENT">Pour un Client</option>
                </select>
              </div>

              {formData.type_creation === 'CLIENT' ? (
                <>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom ou Téléphone Client *</label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={formData.client_nom}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData(f => ({...f, client_nom: v}));
                        searchClients(v);
                      }}
                      onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Tapez le nom, prénom ou téléphone..."
                    />
                    {showClientSuggestions && clientSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                        <div className="px-3 py-2 text-xs text-gray-400 border-b">Clients existants — cliquez pour sélectionner</div>
                        {clientSuggestions.map((c) => (
                          <div
                            key={c.id}
                            onMouseDown={() => selectClient(c)}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          >
                            <div className="font-semibold text-gray-800">{c.nom}</div>
                            {c.telephone && <div className="text-xs text-gray-500">{c.telephone}</div>}
                            {c.email && <div className="text-xs text-gray-400">{c.email}</div>}
                          </div>
                        ))}
                        <div
                          onMouseDown={() => { setShowClientSuggestions(false); }}
                          className="px-4 py-2 text-xs text-blue-600 hover:bg-blue-50 cursor-pointer font-medium"
                        >
                          + Nouveau client — utiliser "{formData.client_nom}"
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom du Client</label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={formData.client_prenom}
                      onChange={(e) => setFormData(f => ({...f, client_prenom: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Ex: BRIGITTE"
                    />
                  </div>
                </>
              ) : (
                <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pour quelle bijouterie *</label>
                  <select
                    value={formData.client_nom}
                    onChange={(e) => setFormData({...formData, client_nom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner une bijouterie</option>
                    {availableBoutiques.map(b => (
                      <option key={b.id} value={b.nom}>{b.nom}</option>
                    ))}
                    <option value="Stock central">Stock central</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Collection</label>
                  <select
                    value={formData.collection_nom}
                    onChange={(e) => setFormData({...formData, collection_nom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner une collection</option>
                    {availableCollections.map(col => (
                      <option key={col.id} value={col.nom}>{col.nom}</option>
                    ))}
                  </select>
                </div>
                </>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  🔑 Référence du bijou
                  <span className="ml-1 text-xs font-normal text-gray-400">(optionnelle — auto-générée si vide)</span>
                </label>
                <input
                  type="text"
                  value={formData.reference_article}
                  onChange={(e) => setFormData({...formData, reference_article: e.target.value.toUpperCase()})}
                  className={`w-full px-3 py-2.5 border-2 rounded-lg font-mono font-bold uppercase text-purple-700 ${
                    formData.reference_article ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                  placeholder="Ex: BAG-NASHA-001 — laisser vide pour auto-générer"
                  autoComplete="off"
                />
                <p className="text-xs mt-1 text-gray-400">
                  Si vide, une référence provisoire sera créée automatiquement (modifiable ensuite)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                <select
                  value={formData.categorie_id}
                  onChange={(e) => setFormData({...formData, categorie_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {availableCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de Bijou *</label>
                <select
                  value={formData.type_bijou}
                  onChange={(e) => setFormData({...formData, type_bijou: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Sélectionner un type</option>
                  {availableTypesBijoux.map(type => (
                    <option key={type.id} value={type.nom}>{type.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Couleur d'Or *</label>
                <select
                  value={formData.couleur_or}
                  onChange={(e) => setFormData({...formData, couleur_or: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="JAUNE">Or Jaune</option>
                  <option value="BLANC">Or Blanc</option>
                  <option value="ROSE">Or Rose</option>
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => { const v = e.target.value; setFormData(f => ({...f, description: v})); }}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Description du bijou..."
                  autoComplete="off"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-red-700 mb-1 flex items-center gap-2">
                  <span>⚠️</span>
                  <span>Note importante (visible en rouge sur la fiche fabrication)</span>
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => { const v = e.target.value; setFormData(f => ({...f, notes: v})); }}
                  className="w-full px-3 py-2 border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-400 bg-red-50"
                  rows="2"
                  placeholder="Ex: Taille 54, gravure intérieure, attention rhodiage renforcé..."
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de livraison souhaitée <span className="text-gray-400 font-normal">(optionnel)</span>
                </label>
                <input
                  type="date"
                  value={formData.date_livraison_souhaitee}
                  onChange={(e) => setFormData({...formData, date_livraison_souhaitee: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              {/* Section Upload Photos */}
              <div className="col-span-2 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                  <ImageIcon className="h-5 w-5 text-indigo-600" />
                  <span>Photos du bijou (optionnel)</span>
                </label>
                
                <div className="space-y-4">
                  {/* Zone d'upload */}
                  <label className="flex flex-col items-center px-6 py-8 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-dashed border-indigo-300 rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-indigo-100 transition-all">
                    <Upload className="h-12 w-12 text-indigo-400 mb-3" />
                    <span className="text-sm font-medium text-gray-700">Cliquez pour ajouter des photos</span>
                    <span className="text-xs text-gray-500 mt-1">PNG, JPG jusqu'à 10MB (plusieurs photos possibles)</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handlePhotosUpload}
                    />
                  </label>

                  {/* Aperçu des photos */}
                  {photosPreviews.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {photosPreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-indigo-300 shadow-md"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                            Photo {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => goToStep(2)}
                className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* Étape 2: Matières et Pierres */}
        {step === 2 && (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border-2 border-blue-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">💎 Matières Premières et Pierres</h3>
              <p className="text-sm text-gray-600">Sélectionnez les matières et pierres pour votre bijou</p>
            </div>
            
            {/* Matières */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h4 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="text-2xl">🥇</span> Matières Premières (Or, Métaux)
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">Ajoutez l'or et les métaux nécessaires</p>
                </div>
                <button
                  onClick={addMatiere}
                  className="px-5 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold flex items-center space-x-2 hover:from-green-600 hover:to-green-700 shadow-lg transition-all"
                >
                  <Plus size={20} />
                  <span>Ajouter une Matière</span>
                </button>
              </div>
              {matieres.map((m, idx) => (
                <div key={idx} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 mb-4 border-2 border-yellow-300 shadow-lg">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🥇</span>
                      <div>
                        <div className="font-bold text-lg text-gray-800">{m.reference || 'Nouvelle Matière'}</div>
                        <div className="text-sm text-gray-600">{m.type || 'Type non défini'}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setMatieres(matieres.filter((_, i) => i !== idx))}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 transition-all"
                    >
                      <Trash2 size={18} />
                      <span>Supprimer</span>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 mb-4">
                    <div className="relative col-span-full">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Référence Fournisseur</label>
                      <input
                        type="text"
                        placeholder="Tapez JPB, COCO..."
                        value={m.reference}
                        onChange={(e) => {
                          const newMatieres = [...matieres];
                          newMatieres[idx].reference = e.target.value;
                          setMatieres(newMatieres);
                          searchMatieres(e.target.value, idx);
                        }}
                        onFocus={() => {
                          if (m.reference && m.reference.length >= 1) {
                            searchMatieres(m.reference, idx);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowMatieresSuggestions(-1);
                            setMatieresSuggestions([]);
                          }, 300);
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base"
                      />
                      
                      {/* Suggestions autocomplete */}
                      {showMatieresSuggestions === idx && matieresSuggestions.length > 0 && (
                        <div className="absolute z-[9999] w-full mt-1 bg-white border-2 border-blue-400 rounded-xl shadow-2xl max-h-96 overflow-y-auto" style={{ position: 'absolute', zIndex: 9999 }}>
                          {matieresSuggestions.map((matiere, mIdx) => {
                            const stockDispo = parseFloat(matiere.quantite_disponible || 0);
                            const stockVide = stockDispo === 0;
                            return (
                            <div
                              key={mIdx}
                              onClick={() => selectMatiere(matiere, idx)}
                              className={`px-5 py-4 cursor-pointer border-b last:border-b-0 transition-all duration-200 ${stockVide ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100'}`}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-bold text-base text-blue-700">{matiere.reference}</div>
                                <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                                  {parseFloat(matiere.prix_achat_unitaire || 0).toFixed(2)} DH/{matiere.unite === 'carat' ? 'ct' : matiere.unite}
                                </div>
                              </div>
                              <div className="text-sm text-gray-700 mb-2 font-medium">
                                {matiere.designation} • {matiere.type_matiere}
                              </div>
                              <div className="flex items-center gap-4">
                                {stockVide ? (
                                  <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg text-sm font-semibold">
                                    ⚠️ Aucun stock — vous pouvez quand même continuer
                                  </div>
                                ) : (
                                  <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-semibold">
                                    📦 Stock: {stockDispo.toFixed(3)} {matiere.unite === 'g' ? 'g' : matiere.unite}
                                  </div>
                                )}
                                {matiere.fournisseur_nom && (
                                  <div className="text-xs text-gray-500">
                                    Fournisseur: {matiere.fournisseur_nom}
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">🏷️ Type de Matière</label>
                      <input
                        type="text"
                        placeholder="OR 18K, ARGENT..."
                        value={m.type}
                        onChange={(e) => {
                          const newMatieres = [...matieres];
                          newMatieres[idx].type = e.target.value;
                          setMatieres(newMatieres);
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base"
                      />
                    </div>
                  </div>
                  
                  {/* Avertissement stock or */}
                  {m.stock_disponible !== undefined && (
                    <div className={`mb-3 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${
                      m.stock_disponible === 0
                        ? 'bg-orange-100 text-orange-700'
                        : parseFloat(m.quantite || 0) > m.stock_disponible
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                    }`}>
                      {m.stock_disponible === 0
                        ? `⚠️ Aucun stock disponible — pensez à réapprovisionner`
                        : parseFloat(m.quantite || 0) > m.stock_disponible
                          ? `⚠️ Stock insuffisant (${m.stock_disponible.toFixed(3)}g dispo)`
                          : `✓ Stock: ${m.stock_disponible.toFixed(3)}g`}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">⚖️ Poids (grammes)</label>
                      <div className="relative">
                        <input
                          type="text" inputMode="decimal" onFocus={e => e.target.select()}
                          step="0.001"
                          placeholder="0.000"
                          value={m.quantite}
                          onChange={(e) => {
                            const newMatieres = [...matieres];
                            newMatieres[idx].quantite = e.target.value;
                            setMatieres(newMatieres);
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base font-semibold"
                        />
                        <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">g</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">💰 Prix Unitaire</label>
                      <div className="relative">
                        <input
                          type="text" inputMode="decimal" onFocus={e => e.target.select()}
                          step="0.01"
                          placeholder="0.00"
                          value={m.prix_unitaire}
                          onChange={(e) => {
                            const newMatieres = [...matieres];
                            newMatieres[idx].prix_unitaire = parseFloat(e.target.value) || 0;
                            setMatieres(newMatieres);
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base font-semibold"
                        />
                        <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">DH/g</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">💵 Prix Total</label>
                      <div className="bg-green-50 border-2 border-green-300 rounded-lg px-4 py-3 text-center">
                        <span className="text-xl font-bold text-green-700">
                          {((m.quantite || 0) * (m.prix_unitaire || 0)).toFixed(2)} DH
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pierres */}
            <PierresEditor pierres={pierres} onChange={setPierres} />

            <div className="flex flex-col sm:flex-row gap-2 justify-between mt-6">
              <button
                onClick={() => goToStep(1)}
                className="w-full sm:w-auto px-6 py-2 border rounded-lg hover:bg-gray-50"
              >
                Précédent
              </button>
              <button
                onClick={() => goToStep(3)}
                className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* Étape 3: Ouvriers et Coûts */}
        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Ouvriers et Coûts de Fabrication</h3>
            
            {/* Section Façon - alternative aux ouvriers */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-bold text-yellow-900">🔨 Façon (main d'œuvre au poids)</h4>
                  <p className="text-xs text-yellow-700 mt-0.5">Alternative aux ouvriers : Coef × Poids or utilisé</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={facon.active}
                    onChange={(e) => setFacon(f => ({...f, active: e.target.checked}))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-yellow-800">Activer la façon</span>
                </label>
              </div>
              {facon.active && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-yellow-800 mb-1">
                      Coefficient façon (DH/g)
                    </label>
                    <input
                      type="number" inputMode="decimal" step="0.01"
                      value={facon.coefficient}
                      onChange={(e) => setFacon(f => ({...f, coefficient: e.target.value}))}
                      className="w-full px-3 py-2 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                      placeholder="Ex: 150 (DH par gramme d'or)"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="bg-yellow-100 rounded-lg p-3 w-full">
                      <div className="text-xs text-yellow-700">Calcul façon :</div>
                      <div className="font-bold text-yellow-900 text-lg">
                        {facon.coefficient && formData.poids_metal_estime
                          ? `${(parseFloat(facon.coefficient) * parseFloat(formData.poids_metal_estime)).toFixed(2)} DH`
                          : '— DH'
                        }
                      </div>
                      <div className="text-xs text-yellow-600">
                        {facon.coefficient} DH/g × {formData.poids_metal_estime || 0}g
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Ouvriers */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h4 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="text-2xl">👷</span> Main d'Œuvre
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">Ajoutez les ouvriers et leur temps de travail</p>
                </div>
                <button
                  onClick={addOuvrier}
                  className="px-5 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold flex items-center space-x-2 hover:from-green-600 hover:to-green-700 shadow-lg transition-all"
                >
                  <Plus size={20} />
                  <span>Ajouter un Ouvrier</span>
                </button>
              </div>
              {ouvriers.map((o, idx) => {
                const ouvrier = availableOuvriers.find(ouv => ouv.id === parseInt(o.ouvrier_id));
                const prixTotal = ouvrier ? (o.temps_minutes || 0) * parseFloat(ouvrier.cout_minute || 0) : 0;
                
                return (
                  <div key={idx} className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 mb-4 border-2 border-green-300 shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">👷</span>
                        <div>
                          <div className="font-bold text-lg text-gray-800">{ouvrier?.nom || 'Sélectionner un ouvrier'}</div>
                          {ouvrier && (
                            <div className="text-sm text-gray-600">{parseFloat(ouvrier.cout_minute || 0).toFixed(2)} DH/minute</div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setOuvriers(ouvriers.filter((_, i) => i !== idx))}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 transition-all"
                      >
                        <Trash2 size={18} />
                        <span>Supprimer</span>
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">👷 Ouvrier</label>
                        <select
                          value={o.ouvrier_id}
                          onChange={(e) => {
                            const newOuvriers = [...ouvriers];
                            newOuvriers[idx].ouvrier_id = e.target.value;
                            setOuvriers(newOuvriers);
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 text-base font-semibold"
                        >
                          <option value="">Sélectionner un ouvrier</option>
                          {availableOuvriers.map(ouv => (
                            <option key={ouv.id} value={ouv.id}>
                              {ouv.nom} ({parseFloat(ouv.cout_minute || 0).toFixed(2)} DH/min)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">⏱️ Temps (minutes)</label>
                        <input
                          type="text" inputMode="decimal" onFocus={e => e.target.select()}
                          placeholder="0"
                          value={o.temps_minutes}
                          onChange={(e) => {
                            const newOuvriers = [...ouvriers];
                            newOuvriers[idx].temps_minutes = parseInt(e.target.value) || 0;
                            setOuvriers(newOuvriers);
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 text-base font-semibold"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4 bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-300 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">💵 Prix Total :</span>
                        <span className="text-2xl font-bold text-green-700">
                          {prixTotal.toFixed(2)} DH
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composants de fabrication */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
              <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span>⚙️</span> Composants de Fabrication
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {[...availableCouts].sort((a, b) => {
                  const oa = a.ordre ?? 99, ob = b.ordre ?? 99;
                  if (oa !== ob) return oa - ob;
                  return a.nom.localeCompare(b.nom, 'fr');
                }).map(cout => {
                  const typeLabel = cout.type_cout === 'POIDS' ? '⚖️ Poids' :
                                   cout.type_cout === 'MINUTE' ? '⏱️ Temps' :
                                   '💰 Prix fixe';
                  const uniteLabel = cout.type_cout === 'POIDS' ? 'g' :
                                    cout.type_cout === 'MINUTE' ? 'min' :
                                    'unité';
                  const ressourceBadge = cout.type_ressource === 'interne'
                    ? <span className="text-xs text-blue-600 font-semibold">🏭 Interne</span>
                    : <span className="text-xs text-orange-600 font-semibold">🔗 Externe</span>;
                  return (
                    <button
                      key={cout.id}
                      onClick={() => addComposant(cout)}
                      className={`px-3 py-2 border-2 rounded-lg text-left transition-all ${
                        composants.find(c => c.cout_id === cout.id)
                          ? cout.type_ressource === 'interne'
                            ? 'bg-blue-100 border-blue-500 font-semibold'
                            : 'bg-orange-100 border-orange-500 font-semibold'
                          : cout.type_ressource === 'interne'
                            ? 'bg-blue-50 border-blue-200 hover:border-blue-400'
                            : 'bg-orange-50 border-orange-200 hover:border-orange-400'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-medium">{cout.nom}</span>
                        {ressourceBadge}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-purple-600 font-semibold">{typeLabel}</span>
                        <span className="text-sm text-gray-600">{cout.cout} DH/{uniteLabel}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {composants.length > 0 && (
                <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
                  <h5 className="font-semibold mb-3 text-purple-800">Composants sélectionnés :</h5>
                  <div className="space-y-3">
                    {composants.map((c, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                        c.type_ressource === 'interne'
                          ? 'bg-blue-50 border-blue-400 border border-blue-200'
                          : 'bg-orange-50 border-orange-400 border border-orange-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-800 flex-1">{c.nom}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                            c.type_ressource === 'interne' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {c.type_ressource === 'interne' ? '🏭 Interne' : '🔗 Externe'}
                          </span>
                          <button
                            onClick={() => removeComposant(idx)}
                            className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-600">
                              {c.type_cout === 'POIDS' ? '⚖️ Poids (g)' :
                               c.type_cout === 'MINUTE' ? '⏱️ Temps (min)' :
                               '📦 Quantité'}
                            </label>
                            <input
                              type="text" inputMode="decimal" onFocus={e => e.target.select()}
                              min="0"
                              step={c.type_cout === 'POIDS' ? '0.01' : c.type_cout === 'MINUTE' ? '1' : '1'}
                              value={c.quantite}
                              onChange={(e) => updateComposantQuantite(idx, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border rounded text-sm"
                              placeholder={c.type_cout === 'POIDS' ? 'Ex: 5.5' : c.type_cout === 'MINUTE' ? 'Ex: 30' : '1'}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">
                              Prix / {c.type_cout === 'POIDS' ? 'g' : c.type_cout === 'MINUTE' ? 'min' : 'unité'}
                            </label>
                            {c.type_cout === 'POIDS' ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text" inputMode="decimal" onFocus={e => e.target.select()}
                                  value={c.prix_unitaire}
                                  onChange={(e) => updateComposantPrix(idx, parseFloat(e.target.value) || 0)}
                                  className="w-24 px-2 py-1 border border-amber-300 rounded text-sm font-semibold bg-amber-50"
                                />
                                <span className="text-[10px] text-gray-400">DH/g</span>
                              </div>
                            ) : (
                              <div className="text-sm font-semibold text-gray-700 py-1">{parseFloat(c.prix_unitaire).toFixed(2)} DH</div>
                            )}
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Total</label>
                            <div className="text-sm font-bold text-purple-700 py-1">{parseFloat(c.prix_total || 0).toFixed(2)} DH</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Packaging */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">📦</span>
                <h4 className="text-xl font-bold text-gray-800">Packaging</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">Sélectionnez le type de packaging pour ce bijou</p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availablePackaging.map(pkg => {
                  const stock = parseInt(pkg.quantite_disponible) || 0;
                  const stockBadge = stock === 0
                    ? { bg: 'bg-red-100', text: 'text-red-700', label: '⚠ À commander' }
                    : stock <= 3
                    ? { bg: 'bg-orange-100', text: 'text-orange-700', label: `⚡ Stock: ${stock}` }
                    : { bg: 'bg-green-100', text: 'text-green-700', label: `✓ Stock: ${stock}` };
                  return (
                  <label
                    key={pkg.id}
                    className={`relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      packagingSelectionne.includes(pkg.id)
                        ? 'bg-purple-100 border-purple-500 shadow-lg'
                        : 'bg-white border-gray-300 hover:border-purple-300 hover:shadow-md'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={packagingSelectionne.includes(pkg.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPackagingSelectionne([...packagingSelectionne, pkg.id]);
                        } else {
                          setPackagingSelectionne(packagingSelectionne.filter(id => id !== pkg.id));
                        }
                      }}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 mt-0.5"
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-semibold text-gray-800 text-sm">{pkg.type}</div>
                      <div className="text-sm font-semibold text-purple-700 mt-0.5">{parseFloat(pkg.prix_vente || 0).toFixed(2)} DH</div>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${stockBadge.bg} ${stockBadge.text}`}>
                        {stockBadge.label}
                      </span>
                    </div>
                  </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2 italic">* Stock insuffisant = commande fournisseur à prévoir — le devis reste valide.</p>
              
              {packagingSelectionne.length > 0 && (
                <div className="mt-4 bg-white rounded-lg p-3 border border-purple-200">
                  <h5 className="font-semibold mb-2 text-purple-700">Packaging sélectionné:</h5>
                  {packagingSelectionne.map(pkgId => {
                    const pkg = availablePackaging.find(p => p.id === pkgId);
                    if (!pkg) return null;
                    const stock = parseInt(pkg.quantite_disponible) || 0;
                    return (
                      <div key={pkgId} className="flex justify-between items-center mb-1">
                        <span className="flex items-center gap-2">
                          {pkg.type}
                          {stock === 0
                            ? <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">À commander</span>
                            : <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">Stock: {stock}</span>
                          }
                        </span>
                        <span className="font-medium text-green-600">{parseFloat(pkg.prix_vente || 0).toFixed(2)} DH</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-between mt-6">
              <button
                onClick={() => goToStep(2)}
                className="w-full sm:w-auto px-6 py-2 border rounded-lg hover:bg-gray-50"
              >
                Précédent
              </button>
              <button
                onClick={() => goToStep(4)}
                className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* Étape 4: Récapitulatif et Prix */}
        {step === 4 && (
          <div className="space-y-6">
            {/* En-tête de la fiche */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl shadow-lg">
              <h2 className="text-3xl font-bold mb-2">📋 FICHE RÉCAPITULATIVE DEVIS</h2>
              <p className="text-blue-100">Détails complets de fabrication - Usage interne</p>
            </div>

            {/* Informations générales */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span>ℹ️</span> Informations Générales
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Type de création:</span>
                  <p className="font-semibold text-gray-800">{formData.type_creation}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">{formData.type_creation === 'CLIENT' ? 'Client' : 'Collection'}:</span>
                  <p className="font-semibold text-gray-800">{formData.type_creation === 'CLIENT' ? formData.client_nom : formData.collection_nom}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Type de bijou:</span>
                  <p className="font-semibold text-gray-800">{formData.type_bijou}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Couleur or:</span>
                  <p className="font-semibold text-gray-800">{formData.couleur_or}</p>
                </div>
                {formData.description && (
                  <div className="col-span-2">
                    <span className="text-sm text-gray-600">Description:</span>
                    <p className="font-semibold text-gray-800">{formData.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Matières premières */}
            {matieres.length > 0 && (
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>🥇</span> Matières Premières
                </h3>
                <div className="space-y-3">
                  {matieres.map((m, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-yellow-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-gray-800">{m.reference}</p>
                          <p className="text-sm text-gray-600">{m.type}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-green-600">{((m.quantite || 0) * (m.prix_unitaire || 0)).toFixed(2)} DH</p>
                          <p className="text-sm text-gray-600">{m.quantite || 0}g × {parseFloat(m.prix_unitaire || 0).toFixed(2)} DH/g</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pierres */}
            {pierres.length > 0 && pierres[0].reference && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>💎</span> Pierres Précieuses
                </h3>
                <div className="space-y-3">
                  {pierres.filter(p => p.reference).map((p, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-gray-800">{p.reference}</p>
                          <p className="text-sm text-gray-600">{p.type}</p>
                          <p className="text-sm text-purple-600 font-semibold">💎 {p.poids_carats} carats × {p.quantite} pcs</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-green-600">{((p.poids_carats || 0) * (p.quantite || 1) * (p.prix_achat_unitaire || 0)).toFixed(2)} DH</p>
                          <p className="text-sm text-gray-600">{parseFloat(p.prix_achat_unitaire || 0).toFixed(2)} DH/carat</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ouvriers */}
            {ouvriers.length > 0 && ouvriers[0].ouvrier_id && (
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>👷</span> Main d'Œuvre
                </h3>
                <div className="space-y-3">
                  {ouvriers.filter(o => o.ouvrier_id).map((o, idx) => {
                    const ouvrier = availableOuvriers.find(av => av.id === parseInt(o.ouvrier_id));
                    return ouvrier ? (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-gray-800">{ouvrier.nom}</p>
                            <p className="text-sm text-gray-600">{o.temps_minutes} minutes</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-green-600">{((o.temps_minutes || 0) * parseFloat(ouvrier.cout_minute || 0)).toFixed(2)} DH</p>
                            <p className="text-sm text-gray-600">{parseFloat(ouvrier.cout_minute || 0).toFixed(2)} DH/min</p>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Façon */}
            {facon.active && facon.coefficient && (
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>🔨</span> Façon (main d'œuvre au poids)
                </h3>
                <div className="bg-white rounded-lg p-4 border border-yellow-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-gray-800">Coefficient façon</p>
                      <p className="text-sm text-gray-600">{facon.coefficient} DH/g × {formData.poids_metal_estime || 0}g</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-green-600">{prixCalcule.facon?.toFixed(2) || '0.00'} DH</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Coûts de fabrication */}
            {coutsFabrication.length > 0 && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>⚙️</span> Coûts de Fabrication
                </h3>
                <div className="space-y-2">
                  {coutsFabrication.map((c, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white rounded-lg p-3 border border-green-200">
                      <span className="font-semibold text-gray-800">{c.nom}</span>
                      <span className="font-bold text-green-600">{parseFloat(c.prix_total || 0).toFixed(2)} DH</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Packaging */}
            {packagingSelectionne.length > 0 && (
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 border-2 border-purple-300 rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>📦</span> Packaging
                </h3>
                <div className="space-y-2">
                  {packagingSelectionne.map(pkgId => {
                    const pkg = availablePackaging.find(p => p.id === pkgId);
                    return pkg ? (
                      <div key={pkgId} className="flex justify-between items-center bg-white rounded-lg p-3 border border-purple-200">
                        <span className="font-semibold text-gray-800">{pkg.type}</span>
                        <span className="font-bold text-purple-600">{parseFloat(pkg.prix_vente || 0).toFixed(2)} DH</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Calcul final des prix avec marges par ligne */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-8 rounded-xl shadow-2xl">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Calculator className="mr-3" size={32} />
                  <h3 className="text-2xl font-bold">CALCUL FINAL</h3>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm bg-gray-700 px-3 py-1.5 rounded-lg">
                  <input
                    type="checkbox"
                    checked={formData.marge_globale_active}
                    onChange={(e) => {
                      setFormData({...formData, marge_globale_active: e.target.checked});
                      if (e.target.checked) {
                        setMargesLignes({ matieres: formData.coefficient_marge, pierres: formData.coefficient_marge, ouvriers: formData.coefficient_marge, composants: formData.coefficient_marge });
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-gray-200 font-medium">Marge globale</span>
                </label>
              </div>
              <p className="text-gray-400 text-xs mb-6">Prix réel · coefficient × · Prix de vente</p>

              {/* En-tête colonnes */}
              <div className="grid grid-cols-12 gap-2 text-xs text-gray-400 font-semibold mb-2 px-1">
                <div className="col-span-4">Catégorie</div>
                <div className="col-span-3 text-right">Prix réel</div>
                <div className="col-span-3 text-center">Coefficient ×</div>
                <div className="col-span-2 text-right">Prix vente</div>
              </div>

              {/* Helper pour boutons +/- */}
              {(() => {
                const step = 0.1;
                const CoefControl = ({ value, onChange }) => (
                  <div className="flex items-center justify-center gap-1">
                    <button type="button"
                      onClick={() => onChange(Math.max(0.1, parseFloat((value - step).toFixed(1))))}
                      className="w-7 h-7 bg-gray-600 hover:bg-gray-500 rounded font-bold text-lg leading-none flex items-center justify-center"
                    >−</button>
                    <span className="w-10 text-center font-bold text-yellow-300 text-sm">{parseFloat(value).toFixed(1)}</span>
                    <button type="button"
                      onClick={() => onChange(parseFloat((value + step).toFixed(1)))}
                      className="w-7 h-7 bg-gray-600 hover:bg-gray-500 rounded font-bold text-lg leading-none flex items-center justify-center"
                    >+</button>
                  </div>
                );

                const setGlobal = (v) => {
                  const coef = parseFloat(v.toFixed(1));
                  setFormData(f => ({...f, coefficient_marge: coef}));
                  setMargesLignes({ matieres: coef, pierres: coef, ouvriers: coef, composants: coef });
                };

                const allItems = [...coutsFabrication, ...composants];
                const itemsInterne = allItems.filter(c => c.type_ressource === 'interne');
                const itemsExterne = allItems.filter(c => c.type_ressource === 'externe');

                const lignes = [
                  { key: 'matieres', label: 'Matières premières', base: prixCalcule.matieres, sub: null },
                  { key: 'pierres',  label: 'Pierres',             base: prixCalcule.pierres,  sub: null },
                  {
                    key: 'ouvriers',
                    label: "Main d'œuvre & Fabrication interne",
                    base: (prixCalcule.ouvriers || 0) + (prixCalcule.facon || 0) + (prixCalcule.fabrication_interne || 0),
                    sub: [
                      ...(prixCalcule.facon > 0 ? [{ nom: facon.description || "Façon", prix: prixCalcule.facon }] : []),
                      ...itemsInterne.map(c => ({ nom: c.nom, prix: c.prix_total })),
                    ]
                  },
                  {
                    key: 'composants',
                    label: 'Prestataires & Composants externes',
                    base: prixCalcule.fabrication_externe || 0,
                    sub: itemsExterne.map(c => ({ nom: c.nom, prix: c.prix_total }))
                  },
                ];

                return (
                  <div className="space-y-2">
                    {formData.marge_globale_active && (
                      <div className="bg-blue-900 bg-opacity-40 rounded-lg p-3 mb-3 grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-4 text-blue-200 font-bold text-sm">Coefficient global</div>
                        <div className="col-span-3 text-right text-gray-300 text-sm">{parseFloat(prixCalcule.total || 0).toFixed(2)} DH</div>
                        <div className="col-span-3">
                          <CoefControl value={formData.coefficient_marge} onChange={setGlobal} />
                        </div>
                        <div className="col-span-2 text-right font-bold text-green-300 text-sm">{parseFloat(prixCalcule.avec_marge || 0).toFixed(2)} DH</div>
                      </div>
                    )}

                    {lignes.map(({ key, label, base, sub }) => {
                      const coef = formData.marge_globale_active ? formData.coefficient_marge : (margesLignes[key] || 2.5);
                      const prixVente = (base || 0) * coef;
                      return (
                        <div key={key}>
                          <div className="grid grid-cols-12 gap-2 items-center py-2 border-b border-gray-700">
                            <div className="col-span-4 text-sm font-semibold text-gray-200">{label}</div>
                            <div className="col-span-3 text-right text-sm text-gray-300">{parseFloat(base || 0).toFixed(2)} DH</div>
                            <div className="col-span-3">
                              {!formData.marge_globale_active ? (
                                <CoefControl
                                  value={margesLignes[key] || 2.5}
                                  onChange={(v) => setMargesLignes(prev => ({...prev, [key]: v}))}
                                />
                              ) : (
                                <div className="text-center text-gray-500 text-xs">—</div>
                              )}
                            </div>
                            <div className="col-span-2 text-right font-bold text-green-300 text-sm">{prixVente.toFixed(2)} DH</div>
                          </div>
                          {sub && sub.length > 0 && (
                            <div className="pl-4 py-1 space-y-0.5">
                              {sub.map((s, i) => (
                                <div key={i} className="flex justify-between text-xs text-gray-400">
                                  <span>{s.nom}</span>
                                  <span>{parseFloat(s.prix || 0).toFixed(2)} DH</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {(prixCalcule.poids_or_total > 0 || matieres.some(m => (m.type||'').toUpperCase().includes('OR') || (m.reference||'').toUpperCase().includes('OR'))) && (
                      <div className="grid grid-cols-12 gap-2 items-center py-2 border-b border-gray-700">
                        <div className="col-span-4 text-sm text-orange-300 flex items-center gap-2">
                          Perte or
                          <select
                            value={formData.perte_or_pct}
                            onChange={e => setFormData({...formData, perte_or_pct: parseFloat(e.target.value)})}
                            className="bg-gray-700 text-orange-300 text-xs rounded px-1 py-0.5 border border-gray-600"
                            onClick={e => e.stopPropagation()}
                          >
                            <option value={0.05}>5%</option>
                            <option value={0.075}>7.5%</option>
                            <option value={0.10}>10%</option>
                          </select>
                        </div>
                        <div className="col-span-3 text-right text-sm text-orange-300">{parseFloat(prixCalcule.perte || 0).toFixed(2)} DH</div>
                        <div className="col-span-3 text-center text-gray-500 text-xs">—</div>
                        <div className="col-span-2 text-right text-sm text-orange-300">{parseFloat(prixCalcule.perte || 0).toFixed(2)} DH</div>
                      </div>
                    )}

                    <div className="border-t-2 border-gray-500 pt-3 grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4 text-lg font-bold">TOTAL</div>
                      <div className="col-span-3 text-right text-yellow-400 font-bold">{parseFloat(prixCalcule.total || 0).toFixed(2)} DH</div>
                      <div className="col-span-3"></div>
                      <div className="col-span-2 text-right text-green-300 font-bold text-lg">{parseFloat(prixCalcule.avec_marge || 0).toFixed(2)} DH</div>
                    </div>

                    {/* Resume du calcul */}
                    <div className="bg-gray-800 bg-opacity-60 rounded-xl p-4 mt-3 border border-gray-600">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Resume du calcul</h4>
                      <div className="space-y-1 text-sm">
                        {lignes.filter(l => (l.base || 0) > 0).map(l => {
                          const coef = formData.marge_globale_active ? parseFloat(formData.coefficient_marge) : (margesLignes[l.key] || 2.5);
                          return (
                            <div key={l.key} className="flex justify-between text-gray-300">
                              <span>{l.label}</span>
                              <span>{parseFloat(l.base || 0).toFixed(2)} x {coef.toFixed(2)} = <span className="text-green-300 font-bold">{(parseFloat(l.base || 0) * coef).toFixed(2)} DH</span></span>
                            </div>
                          );
                        })}
                        {prixCalcule.perte > 0 && (
                          <div className="flex justify-between text-orange-300">
                            <span>Perte or ({prixCalcule.poids_or_total?.toFixed(1)}g x {(prixCalcule.perte_pct * 100).toFixed(0)}% x {parseFloat(prixCalcule.prix_or_utilise || 0).toFixed(0)} DH/g)</span>
                            <span className="font-bold">{parseFloat(prixCalcule.perte).toFixed(2)} DH</span>
                          </div>
                        )}
                        <div className="border-t border-gray-600 pt-1 flex justify-between text-yellow-300 font-bold">
                          <span>Prix reel total</span>
                          <span>{parseFloat(prixCalcule.total).toFixed(2)} DH</span>
                        </div>
                        <div className="flex justify-between text-green-300 font-bold text-base">
                          <span>Prix de vente final</span>
                          <span>{parseFloat(prixCalcule.avec_marge).toFixed(2)} DH</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-5 rounded-xl mt-4 flex justify-between items-center">
                      <span className="text-xl font-bold">PRIX DE VENTE FINAL</span>
                      <span className="text-3xl font-bold">{parseFloat(prixCalcule.avec_marge || 0).toFixed(2)} DH</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-between mt-8">
              <button
                onClick={() => goToStep(3)}
                className="w-full sm:w-auto px-8 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 font-semibold text-lg"
              >
                ← Précédent
              </button>
              <button
                onClick={handleSubmit}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 font-bold text-lg shadow-lg"
              >
                ✓ Valider le Devis
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateDevis;
